import { useFieldApi } from 'informed';
import { type FC, useEffect, useMemo, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { ThemeMode } from '@/helpers/enums';
import { mailMask } from '@/helpers/masks';
import type { WithTestId } from '@/helpers/testIds';
import { validateEmail } from '@/helpers/validators';
import type { RegistrationConfig } from '@/modules/registration/helpers/types';
import { typography } from '@/styles/helpers';
import { IconComponent } from '../IconComponent';
import { ScLink } from './groups/styled';
import { correctEmailDomain, DOMAINS_OPTIONS } from './helpers';
import { Input } from './Input';
import { OptionsBlock } from './primitives/SelectBlock';

interface InputEmailI extends WithTestId {
  styleScheme?: RegistrationConfig['styleScheme'];
  withInsideLabel?: boolean;
}

export const InputEmail: FC<InputEmailI> = ({
  styleScheme,
  withInsideLabel,
  testId,
}) => {
  const { t } = useTranslation('common');

  const { getValue, setValue, validate, getTouched } = useFieldApi('email');

  const [focused, setFocused] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [correctionWarning, setCorrectionWarning] = useState<
    string | undefined
  >();

  const blurTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const onFocus = () => setFocused(true);
  const onBlur = () => {
    blurTimeoutRef.current = setTimeout(() => {
      setFocused(false);
    }, 300);

    handleValidateCorrection();
  };

  const handleValidateCorrection = () => {
    const value = getValue() as string;

    const correctedEmail = correctEmailDomain(value ?? '');
    setCorrectionWarning(correctedEmail);
  };

  const handleCorrectValue = () => {
    if (!correctionWarning) return;

    handleSetValue(correctionWarning);
    validate();
  };

  const handleSetValue = (value: string) => {
    setValue(value);
    setInputValue(value);
    setFocused(false);
    setCorrectionWarning('');
  };

  useEffect(
    () => () => {
      if (blurTimeoutRef.current) {
        clearTimeout(blurTimeoutRef.current);
      }
    },
    []
  );

  useEffect(() => {
    if (!focused && getTouched()) validate();
  }, [focused, getTouched, validate]);

  const options = useMemo(() => {
    const inputValueParts = inputValue.replace(/@+/, '@').split('@');
    const inputValueDomain = inputValueParts[1];

    return DOMAINS_OPTIONS.filter(
      (domain) =>
        domain !== inputValueDomain && domain.startsWith(inputValueDomain)
    ).map((domain) => {
      const value = `${inputValueParts[0]}@${domain}`;
      return { name: value, value };
    });
  }, [inputValue]);

  const isOptionsVisible = useMemo(() => {
    const specialSymbolCount = (inputValue.match(/@/g) || []).length;
    return (
      specialSymbolCount < 2 &&
      inputValue.includes('@') &&
      focused &&
      !!options.length
    );
  }, [focused, inputValue, options]);

  return (
    <ScInputEmail>
      <Input
        autoComplete="email"
        internalLabel={withInsideLabel ? t('auth.registration.form.email') : ''}
        name="email"
        onBlur={onBlur}
        onChange={({ target }) => setInputValue(target.value)}
        onFocus={onFocus}
        parser={mailMask}
        placeholder={withInsideLabel ? '' : t('auth.registration.form.email')}
        styleScheme={styleScheme}
        testId={testId}
        validate={validateEmail}
        validateOn="submit"
        withInsideLabel={withInsideLabel}
      />
      {isOptionsVisible && (
        <div className="options">
          <OptionsBlock
            mode={ThemeMode.Light}
            onChange={(provider) => handleSetValue(provider as string)}
            options={options}
            styleScheme={styleScheme}
          />
        </div>
      )}
      {correctionWarning && (
        <div className="correctionWarning">
          <IconComponent name="tooltipIcon" size="18px" />
          <p>
            <Trans
              components={{
                btn: <ScLink className="link" onClick={handleCorrectValue} />,
                b: <strong />,
              }}
              i18nKey="auth.registration.form.emailCorrection"
              ns="common"
              values={{ domain: correctionWarning }}
            />
          </p>
        </div>
      )}
    </ScInputEmail>
  );
};

export const ScInputEmail = styled.div`
  position: relative;

  & .options {
    position: absolute;
    z-index: 6;
    left: 0;
    right: 0;
  }

  & .correctionWarning {
    display: flex;
    padding: 4px 12px;
    margin-top: 4px;
    align-items: center;
    gap: 8px;
    align-self: stretch;
    border-radius: 16px;
    background: rgba(79, 121, 171, 0.2);
    min-height: 32px;

    .link {
      text-decoration: underline;
      ${typography['caption1bold']};
    }

    p {
      ${typography['caption1regular']};
    }

    i {
      width: 16px;
      height: 16px;
    }
  }
`;
